import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type { AccountDto, CashExpenseDto, CreateCashExpenseRequest } from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney, parseAmount } from './money';
import Loading from './Loading';
import Hint from './Hint';
import ListFilter, { useListFilter } from './ListFilter';
import { inDateRange, matchesSearch } from './search';

interface Props {
  companyId: string;
  currency: string;
  accounts: readonly AccountDto[];
  canRecord: boolean;
  onSessionLost: () => void;
}

interface DraftLine {
  id: number;
  description: string;
  amount: string;
  accountCode: string;
}

let nextLineId = 1;
const emptyLine = (): DraftLine => ({
  id: nextLineId++,
  description: '',
  amount: '',
  accountCode: '',
});

const CASH_SUBLEDGERS = new Set(['cashbox', 'bank_or_wallet']);

const ISSUE_LABELS: Record<string, string> = {
  EXPENSE_LINE_ACCOUNT: 'الحساب يجب أن يكون حساب مصروف',
  PAYMENT_SOURCE_ACCOUNT: 'حساب الصرف يجب أن يكون خزينة أو بنكًا',
  CASHBOX_NEGATIVE: 'رصيد الخزينة لا يكفى',
  CASHBOX_NOT_CONFIGURED: 'لا خزينة معرّفة على هذا الحساب',
  LINE_NO_AMOUNT: 'المبلغ يجب أن يكون موجبًا',
  PERIOD_CLOSED: 'الفترة المالية مقفلة',
};

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

/**
 * المصروف النقدى المباشر: يُدفع لحظته من خزينة أو بنك بلا فاتورة مورد. يُرحَّل فورًا،
 * والخزينة لا تصرف أكثر من رصيدها.
 */
export default function CashExpenses({
  companyId,
  currency,
  accounts,
  canRecord,
  onSessionLost,
}: Props) {
  const [expenses, setExpenses] = useState<CashExpenseDto[] | null>(null);
  const [filter, setFilter] = useListFilter('', 'CashExpenses');
  const shownExpenses = (expenses ?? []).filter(
    (expense) =>
      matchesSearch(filter.query, [
        expense.number,
        expense.payee,
        expense.externalReference,
        expense.memo,
        expense.journalNumber,
        expense.total,
      ]) && inDateRange(expense.expenseDate, filter.from, filter.to),
  );
  const [expenseDate, setExpenseDate] = useState(today);
  const [sourceAccountCode, setSourceAccountCode] = useState('');
  const [payee, setPayee] = useState('');
  const [lines, setLines] = useState<DraftLine[]>(() => [emptyLine()]);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState<{ code: string; message: string; lineIndex?: number }[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<CashExpenseDto | null>(null);

  const cashAccounts = useMemo(
    () =>
      accounts.filter(
        (account) =>
          account.isPostable &&
          account.subledger !== null &&
          CASH_SUBLEDGERS.has(account.subledger),
      ),
    [accounts],
  );
  const expenseAccounts = useMemo(
    () => accounts.filter((account) => account.isPostable && account.accountClass === 'expense'),
    [accounts],
  );

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        setIssues(caught.problem.issues ?? []);
        setFailure(
          caught.problem.issues?.length ? null : (caught.problem.detail ?? caught.problem.title),
        );
        return;
      }
      setFailure(caught instanceof Error ? caught.message : String(caught));
    },
    [onSessionLost],
  );

  const load = useCallback(() => {
    api.cashExpenses(companyId).then(setExpenses).catch(handleError);
  }, [companyId, handleError]);

  useEffect(load, [load]);

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setIssues([]);
    setFailure(null);
    setRecorded(null);
  }

  function updateLine(id: number, patch: Partial<DraftLine>): void {
    setLines((current) => current.map((line) => (line.id === id ? { ...line, ...patch } : line)));
    touched();
  }

  const amounts = lines.map((line) => parseAmount(line.amount));
  const total = amounts.every((amount) => amount !== null) ? Money.sum(amounts as string[]) : null;
  const ready =
    sourceAccountCode !== '' &&
    lines.every(
      (line, index) =>
        line.description.trim() !== '' &&
        line.accountCode !== '' &&
        amounts[index] !== null &&
        Money.of(amounts[index] as string).isPositive(),
    ) &&
    total !== null;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setIssues([]);
    setFailure(null);
    const body: CreateCashExpenseRequest = {
      expenseDate,
      sourceAccountCode,
      ...(payee.trim() ? { payee: payee.trim() } : {}),
      lines: lines.map((line, index) => ({
        description: line.description.trim(),
        amount: amounts[index] ?? line.amount,
        accountCode: line.accountCode,
      })),
    };
    try {
      const expense = await api.createCashExpense(companyId, idempotencyKey, body);
      setRecorded(expense);
      setLines([emptyLine()]);
      setPayee('');
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      handleError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const generalIssues = issues.filter((issue) => issue.lineIndex === undefined);

  return (
    <>
      {canRecord && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>مصروف نقدى</h2>
          <div className="form-row">
            <label>
              التاريخ
              <input
                type="date"
                value={expenseDate}
                onChange={(event) => {
                  setExpenseDate(event.target.value);
                  touched();
                }}
              />
            </label>
            <label className="grow">
              دُفع من
              <select
                value={sourceAccountCode}
                onChange={(event) => {
                  setSourceAccountCode(event.target.value);
                  touched();
                }}
              >
                <option value="">— خزينة أو بنك —</option>
                {cashAccounts.map((account) => (
                  <option key={account.code} value={account.code}>
                    {account.code} — {account.nameAr}
                  </option>
                ))}
              </select>
              <Hint>الخزينة أو البنك الذى خرجت منه الفلوس.</Hint>
            </label>
            <label className="grow">
              لمن دُفع
              <input
                value={payee}
                maxLength={200}
                onChange={(event) => {
                  setPayee(event.target.value);
                  touched();
                }}
              />
              <Hint>اسم من استلم الفلوس، مثل: شركة الكهرباء أو اسم العامل.</Hint>
            </label>
          </div>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>البيان</th>
                  <th>حساب المصروف</th>
                  <th>المبلغ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={line.id}>
                    <td>
                      <input
                        value={line.description}
                        maxLength={500}
                        onChange={(event) =>
                          updateLine(line.id, { description: event.target.value })
                        }
                      />
                      {issues
                        .filter((issue) => issue.lineIndex === index)
                        .map((issue, i) => (
                          <p key={i} className="error">
                            {ISSUE_LABELS[issue.code] ?? issue.message}
                          </p>
                        ))}
                    </td>
                    <td>
                      <select
                        value={line.accountCode}
                        onChange={(event) =>
                          updateLine(line.id, { accountCode: event.target.value })
                        }
                      >
                        <option value="">— اختر حسابًا —</option>
                        {expenseAccounts.map((account) => (
                          <option key={account.code} value={account.code}>
                            {account.code} — {account.nameAr}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        inputMode="decimal"
                        value={line.amount}
                        onChange={(event) => updateLine(line.id, { amount: event.target.value })}
                      />
                    </td>
                    <td>
                      {lines.length > 1 && (
                        <button
                          type="button"
                          className="link"
                          onClick={() => {
                            setLines((current) => current.filter((item) => item.id !== line.id));
                            touched();
                          }}
                        >
                          حذف
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="closing">
                  <td colSpan={2}>الإجمالى</td>
                  <td className="num-cell total">{total ? formatMoney(total) : '—'}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {generalIssues.map((issue, i) => (
            <p key={i} className="error">
              {ISSUE_LABELS[issue.code] ?? issue.message}
            </p>
          ))}
          {failure && <p className="error">{failure}</p>}
          {recorded && (
            <p className="sub">
              سُجل المصروف {recorded.number} بمبلغ {formatMoney(recorded.total)} {currency} — قيده{' '}
              {recorded.journalNumber}.
            </p>
          )}
          <div className="form-actions sticky">
            <button type="submit" className="primary" disabled={submitting || !ready}>
              {submitting ? 'جارى التسجيل…' : 'تسجيل المصروف'}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setLines((current) => [...current, emptyLine()]);
                touched();
              }}
            >
              سطر جديد
            </button>
            <Hint block>
              يخصم المبلغ من الخزينة أو البنك ويظهر فى مصروفات الشهر. لو للمصروف فاتورة مورد، سجّله
              من «فواتير الموردين» بدلاً من هنا. بعد الترحيل لا يُعدَّل ولا يُحذف — الخطأ يُصحَّح
              بمستند عكسى، حتى يبقى تاريخ كل مبلغ واضحًا.
            </Hint>
          </div>
        </form>
      )}

      <div className="panel">
        <h2>المصروفات النقدية</h2>
        {expenses === null && !failure && <Loading />}
        {expenses?.length === 0 && <p className="sub">لا مصروفات نقدية بعد.</p>}
        {expenses && expenses.length > 0 && (
          <ListFilter
            filter={filter}
            onChange={setFilter}
            placeholder="دوّر بالرقم أو المستفيد أو البيان أو المبلغ"
            dates
            shown={shownExpenses.length}
            total={expenses.length}
          />
        )}
        {expenses && expenses.length > 0 && shownExpenses.length === 0 && (
          <p className="sub">مفيش نتايج للبحث ده.</p>
        )}
        {expenses && shownExpenses.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الرقم</th>
                  <th>التاريخ</th>
                  <th>لمن</th>
                  <th>البنود</th>
                  <th>المبلغ</th>
                  <th>القيد</th>
                </tr>
              </thead>
              <tbody>
                {shownExpenses.map((expense) => (
                  <tr key={expense.id}>
                    <td className="mono">{expense.number}</td>
                    <td>{expense.expenseDate}</td>
                    <td>{expense.payee ?? ''}</td>
                    <td>{expense.lines.map((line) => line.description).join('، ')}</td>
                    <td className="num-cell">{formatMoney(expense.total)}</td>
                    <td className="mono">{expense.journalNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
